'use client'

import { ArrowDown as MoveDown } from '@carbon/icons-react'
import Link from 'next/link'

const industriesData = [
  {
    id: 1,
    slug: 'education',
    title: 'Education',
    description:
      'Transform learning with intelligent platforms, student management systems, and AI-powered solutions that elevate educational outcomes for modern institutions.',
    thumbnail: '/illustrations/lms.webp',
  },
  {
    id: 2,
    slug: 'logistics',
    title: 'Logistics',
    description:
      'Optimize supply chain operations with intelligent logistics solutions through fleet management systems, route optimization tools, and AI-powered platforms that deliver efficiency and cost savings.',
    thumbnail: '/illustrations/intelqi_udc.webp',
  },
  {
    id: 3,
    slug: 'retail',
    title: 'Retail',
    description:
      'Transform retail experiences with modern e-commerce platforms, omnichannel solutions, and intelligent inventory systems that drive sales and customer loyalty.',
    thumbnail: '/illustrations/payroll.webp',
  },
  {
    id: 4,
    slug: 'fintech',
    title: 'Fintech',
    description:
      'Power the future of finance with secure, scalable fintech solutions that transform how people manage money through digital banking, payments, and AI-driven services.',
    thumbnail: '/illustrations/accounting.webp',
  },
  {
    id: 5,
    slug: 'healthcare',
    title: 'Healthcare',
    description:
      'Transform patient care with cutting-edge healthcare technology through secure EHR systems, telemedicine platforms, and AI-powered diagnostic tools that improve outcomes and save lives.',
    thumbnail: '/illustrations/hrms.webp',
  },
  {
    id: 6,
    slug: 'corporate',
    title: 'Corporate',
    description:
      'Empower your enterprise with comprehensive business solutions through integrated ERP systems, HRMS platforms, and intelligent automation tools that drive operational excellence and growth.',
    thumbnail: '/illustrations/accounting.webp',
  },
]

export default function IndustriesPage() {
  return (
    <main className="relative">
      {/* Hero Section with Grid Background */}
      <section className="relative overflow-hidden hero-adaptive-bg">
        <div className="relative py-16 lg:py-24">
          <div className="container px-6 lg:px-8">
            <div className="mx-auto max-w-4xl text-center">
              <div className="space-y-6 relative z-10">
                <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-normal text-foreground leading-tight animate-fade-in">
                  ERP and Technology Solutions by Industry
                </h1>
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed animate-fade-in-delay max-w-2xl mx-auto">
                  Discover how Euclideum delivers innovative technology
                  solutions tailored for diverse industries, driving
                  transformation and growth across sectors.
                </p>
                <Link
                  href="#industries"
                  className="inline-flex items-center gap-2 pt-4 group animate-bounce-slow"
                >
                  <div className="bg-foreground/10 backdrop-blur-sm p-2 rounded-full group-hover:bg-foreground/20 transition-all">
                    <MoveDown className="h-5 w-5 text-foreground" />
                  </div>
                  <span className="text-sm text-muted-foreground font-medium">
                    Scroll to explore
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Grid Section */}
      <section
        id="industries"
        className="bg-background pt-6 lg:pt-8 pb-12 lg:pb-16 scroll-mt-20"
      >
        <div className="container px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {industriesData.map((industry) => (
                <Link
                  key={industry.id}
                  href={`/industries/${industry.slug}`}
                  className="group bg-card rounded-[32px] overflow-hidden border border-border hover:shadow-xl hover:border-emerald-600/30 dark:hover:border-primary-active/30 transition-all duration-300 capability-card"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={industry.thumbnail}
                      alt={industry.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  <div className="p-8">
                    <h2 className="text-2xl font-bold text-foreground mb-4">
                      {industry.title}
                    </h2>
                    <p className="text-muted-foreground leading-relaxed mb-6 min-h-[100px]">
                      {industry.description}
                    </p>
                    <span className="inline-block text-foreground font-semibold text-base relative">
                      Learn More
                      <span className="absolute left-0 bottom-0 w-0 h-0.5 bg-foreground transition-all duration-300 group-hover:w-full"></span>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <style
        dangerouslySetInnerHTML={{
          __html: `
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(10px); }
        }
        .animate-fade-in { animation: fade-in 0.6s ease-out; }
        .animate-fade-in-delay { animation: fade-in 0.6s ease-out 0.2s both; }
        .animate-bounce-slow { animation: bounce-slow 2s ease-in-out infinite; }
        .capability-card { background: hsl(var(--card)); }
        .capability-card:hover { background: hsl(var(--accent)); }
      `,
        }}
      />
    </main>
  )
}
